import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, Book, Search } from 'lucide-react';
import { novelAPI } from '../../services/api';
import toast from 'react-hot-toast';
import './Admin.css';

const NovelList = () => {
  const [novels, setNovels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchNovels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const fetchNovels = async () => {
    setIsLoading(true);
    try {
      const response = await novelAPI.getAll({ page: currentPage, limit: 20 });
      setNovels(response.data || []);
      setPagination(response.pagination);
    } catch (error) {
      toast.error('Failed to load novels');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;
    
    try {
      await novelAPI.delete(id);
      toast.success('Novel deleted');
      fetchNovels();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const filteredNovels = novels.filter(novel => 
    novel.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    novel.author?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const badges = {
      ongoing: { color: '#3b82f6', label: 'Ongoing' },
      completed: { color: '#22c55e', label: 'Completed' },
      hiatus: { color: '#f59e0b', label: 'On Hiatus' },
      dropped: { color: '#ef4444', label: 'Dropped' }
    };
    const badge = badges[status] || badges.ongoing;
    return (
      <span style={{
        padding: '4px 10px',
        background: `${badge.color}20`,
        color: badge.color,
        borderRadius: '20px',
        fontSize: '0.8rem'
      }}>
        {badge.label}
      </span>
    );
  };

  return (
    <div className="novel-list-admin">
      <div className="admin-page-header">
        <h1>Novel Management</h1>
        <Link to="/admin/novels/new" className="btn-add">
          <Plus size={18} />
          Add Novel
        </Link>
      </div>

      <div className="admin-filters">
        <div className="search-box" style={{ position: 'relative', maxWidth: '400px' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder="Search by title or author..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ paddingLeft: '40px', width: '100%' }}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="admin-loading">
          <div className="loading-spinner"></div>
        </div>
      ) : filteredNovels.length > 0 ? (
        <>
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Novel</th>
                  <th>Author</th>
                  <th>Chapters</th>
                  <th>Views</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredNovels.map((novel) => (
                  <tr key={novel._id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '50px',
                          height: '65px',
                          borderRadius: '6px',
                          overflow: 'hidden',
                          background: 'var(--bg-dark)'
                        }}>
                          {novel.cover ? (
                            <img src={novel.cover} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                              <Book size={20} />
                            </div>
                          )}
                        </div>
                        <div>
                          <strong>{novel.title}</strong>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                            /{novel.slug}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>{novel.author}</td>
                    <td>{novel.chapterCount || 0}</td>
                    <td>{novel.stats?.views?.toLocaleString() || 0}</td>
                    <td>{getStatusBadge(novel.status)}</td>
                    <td>
                      <div className="table-actions">
                        <Link to={`/novel/${novel.slug}`} className="btn-edit" title="View">
                          <Eye size={16} />
                        </Link>
                        <Link to={`/admin/novels/edit/${novel._id}`} className="btn-edit" title="Edit">
                          <Edit size={16} />
                        </Link>
                        <button 
                          className="btn-delete"
                          title="Delete"
                          onClick={() => handleDelete(novel._id, novel.title)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination && pagination.totalPages > 1 && (
            <div className="admin-pagination">
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  className={`page-btn ${page === currentPage ? 'active' : ''}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="admin-empty">
          <Book size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
          <p>No novels found</p>
        </div>
      )}
    </div>
  );
};

export default NovelList;
